// Color Utilities for Accessibility and Validation

import { ColorValidationResult } from '../types/colorTypes';

/**
 * Validate if a string is a valid hex color
 */
export const isValidHexColor = (color: string): boolean => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
};

/**
 * Convert hex to RGB values
 */
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Calculate relative luminance of a color
 */
export const getLuminance = (color: string): number => {
  const rgb = hexToRgb(color);
  if (!rgb) return 0;
  
  const { r, g, b } = rgb;
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Calculate contrast ratio between two colors
 */
export const getContrastRatio = (color1: string, color2: string): number => {
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  
  return (brightest + 0.05) / (darkest + 0.05);
};

/**
 * Generate accessible text color for a given background
 */
export const generateAccessibleTextColor = (backgroundColor: string): string => {
  const whiteContrast = getContrastRatio(backgroundColor, '#ffffff');
  const blackContrast = getContrastRatio(backgroundColor, '#000000');
  
  // WCAG AA requires 4.5:1 for normal text, 3:1 for large text
  if (whiteContrast >= 4.5) return '#ffffff';
  if (blackContrast >= 4.5) return '#000000';
  
  // Fall back to higher contrast option
  return whiteContrast > blackContrast ? '#ffffff' : '#000000';
};

/**
 * Generate accessible border color for a given background
 */
export const generateAccessibleBorderColor = (backgroundColor: string): string => {
  const rgb = hexToRgb(backgroundColor);
  if (!rgb) return '#666666';
  
  const { r, g, b } = rgb;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  
  // If background is light, make border darker
  if (brightness > 128) {
    return darkenColor(backgroundColor, 0.3);
  } else {
    return lightenColor(backgroundColor, 0.3);
  }
};

/**
 * Lighten a color by a percentage
 */
export const lightenColor = (color: string, amount: number): string => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  
  const { r, g, b } = rgb;
  const newR = Math.min(255, Math.round(r + (255 - r) * amount));
  const newG = Math.min(255, Math.round(g + (255 - g) * amount));
  const newB = Math.min(255, Math.round(b + (255 - b) * amount));
  
  return `#${[newR, newG, newB].map(x => x.toString(16).padStart(2, '0')).join('')}`;
};

/**
 * Darken a color by a percentage
 */
export const darkenColor = (color: string, amount: number): string => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  
  const { r, g, b } = rgb;
  const newR = Math.max(0, Math.round(r * (1 - amount)));
  const newG = Math.max(0, Math.round(g * (1 - amount)));
  const newB = Math.max(0, Math.round(b * (1 - amount)));
  
  return `#${[newR, newG, newB].map(x => x.toString(16).padStart(2, '0')).join('')}`;
};

/**
 * Validate accessibility of a color combination
 */
export const validateColorAccessibility = (
  backgroundColor: string, 
  textColor: string, 
  borderColor: string
): ColorValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // Validate hex format
  if (!isValidHexColor(backgroundColor)) {
    errors.push('Invalid background color format');
  }
  if (!isValidHexColor(textColor)) {
    errors.push('Invalid text color format');
  }
  if (!isValidHexColor(borderColor)) {
    errors.push('Invalid border color format');
  }
  
  if (errors.length > 0) {
    return { isValid: false, errors, warnings };
  }
  
  // Check contrast ratios
  const textContrast = getContrastRatio(backgroundColor, textColor);
  const borderContrast = getContrastRatio(backgroundColor, borderColor);
  
  if (textContrast < 4.5) {
    errors.push(`Text contrast ratio (${textContrast.toFixed(2)}) is below WCAG AA standard (4.5)`);
  } else if (textContrast < 7) {
    warnings.push(`Text contrast ratio (${textContrast.toFixed(2)}) is below WCAG AAA standard (7)`);
  }
  
  if (borderContrast < 3) {
    warnings.push(`Border contrast ratio (${borderContrast.toFixed(2)}) is below recommended minimum (3)`);
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Generate a random accessible color
 */
export const generateRandomAccessibleColor = (): string => {
  const colors = [
    '#e3f2fd', '#f3e5f5', '#e8f5e8', '#fff3e0', '#fce4ec', '#e0f2f1',
    '#f1f8e9', '#fff8e1', '#f3e5f5', '#e8eaf6'
  ];
  
  return colors[Math.floor(Math.random() * colors.length)];
};